import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
  setData: (d: T) => void;
}

/** Run an async loader; keeps the previous data while refetching (no layout flash). */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let live = true;
    setLoading(true);
    fnRef
      .current()
      .then((d) => {
        if (!live) return;
        setData(d);
        setError(undefined);
      })
      .catch((e: Error) => {
        if (live) setError(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}

/** Poll a function on an interval while `active`; stops when `done(result)` is true. */
export function usePolling<T>(fn: () => Promise<T>, opts: { intervalMs: number; active: boolean; done?: (r: T) => boolean }) {
  const [result, setResult] = useState<T>();
  const [error, setError] = useState<Error>();
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const doneRef = useRef(opts.done);
  doneRef.current = opts.done;

  useEffect(() => {
    if (!opts.active) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const run = async () => {
      try {
        const r = await fnRef.current();
        if (!live) return;
        setResult(r);
        setError(undefined);
        if (doneRef.current?.(r)) return;
      } catch (e) {
        if (!live) return;
        setError(e as Error);
      }
      timer = setTimeout(run, opts.intervalMs);
    };
    void run();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [opts.active, opts.intervalMs]);

  return { result, error };
}

export function useInterval(cb: () => void, ms: number | null) {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    if (ms === null) return;
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

/** Per-viewer UI preference in localStorage; failures (private mode, blocked storage) are non-fatal. */
export function useLocalPref<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (nv: T) => {
      setV(nv);
      try {
        localStorage.setItem(key, JSON.stringify(nv));
      } catch {
        /* ignore */
      }
    },
    [key],
  );
  return [v, set];
}
